import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ADMIN_ENDPOINTS, ENDPOINTS, adminFetch } from '../config';
import AdminImageUpload from './AdminImageUpload';
import AdminTechnologyTaxonomy from './AdminTechnologyTaxonomy';
import AdminSectionEditor from './AdminSectionEditor';
import './Admin.css';

const UPLOAD_PREFIX = '/admin/uploads/';
function imgUrl(src) {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return src.startsWith('uploads/') ? `/admin/${src}` : UPLOAD_PREFIX + src.replace(/^uploads\//, '');
}

/** Parse section content into column count and array of column HTML strings */
function parseSectionContent(content) {
  if (!content || !content.trim()) return { columnCount: 1, columnContents: [''] };
  const trimmed = content.trim();
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div id="_root">' + trimmed + '</div>', 'text/html');
  const root = doc.getElementById('_root');
  if (!root) return { columnCount: 1, columnContents: [trimmed] };
  const wrapper = root.querySelector('.project-cols-2, .project-cols-3');
  if (wrapper) {
    const cols = wrapper.querySelectorAll('.project-col');
    if (cols.length >= 2) {
      const columnContents = Array.from(cols).map((c) => c.innerHTML.trim());
      return { columnCount: columnContents.length, columnContents };
    }
  }
  return { columnCount: 1, columnContents: [trimmed] };
}

/** Build section content HTML from column count and column HTML strings */
function buildSectionContent(columnCount, columnContents) {
  const n = Math.max(1, Math.min(3, columnCount));
  const parts = (columnContents || ['']).slice(0, n);
  while (parts.length < n) parts.push('');
  if (n === 1) return parts[0] || '';
  const gridStyle =
    n === 2
      ? 'display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin:1rem 0;'
      : 'display:grid;grid-template-columns:1fr 1fr 1fr;gap:1rem;margin:1rem 0;';
  const colClass = 'project-cols project-cols-' + n;
  const colDivs = parts.map((c) => '<div class="project-col">' + (c || '') + '</div>').join('');
  return '<div class="' + colClass + '" style="' + gridStyle + '">' + colDivs + '</div>';
}

export default function AdminProjects() {
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [technologyTerms, setTechnologyTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    category_id: '', title: '', summary: '', image_url: '', project_link: '', collaborators: '',
    technology_ids: [], technology_new: '', start_date: '', end_date: '', sort_order: 0, is_featured: 0,
    sections: [],
  });
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragProjectIndex, setDragProjectIndex] = useState(null);
  const [dragOverProjectIndex, setDragOverProjectIndex] = useState(null);
  const navigate = useNavigate();
  const { projectId } = useParams();

  const reorderProjects = async (newOrder) => {
    const orders = newOrder.map((p, i) => ({ id: p.id, sort_order: i }));
    const res = await adminFetch(ADMIN_ENDPOINTS.reorderProjects, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orders }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [catRes, projRes, techRes] = await Promise.all([
        fetch(ENDPOINTS.projectCategories),
        fetch(ENDPOINTS.projects),
        fetch(ENDPOINTS.fetchTechnologyTerms),
      ]);
      const cats = await catRes.json();
      const projs = await projRes.json();
      // Normalize featured flag to a numeric 0/1 so the checkbox and list behave consistently
      const normalizedProjects = (Array.isArray(projs) ? projs : []).map((p) => ({
        ...p,
        is_featured: Number(p.is_featured) ? 1 : 0,
      }));
      let tech = [];
      try {
        tech = await techRes.json();
      } catch {
        tech = [];
      }
      setCategories(Array.isArray(cats) ? cats : []);
      setProjects(normalizedProjects);
      setTechnologyTerms(Array.isArray(tech) ? tech : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!projectId) {
      setEditId(null);
      return;
    }
    if (projectId === 'new') {
      // New project form on its own page
      setEditId(null);
      setForm((f) => ({
        category_id: categories[0]?.id || '',
        title: '',
        summary: '',
        image_url: '',
        project_link: '',
        collaborators: '',
        technology_ids: [],
        technology_new: '',
        start_date: '',
        end_date: '',
        sort_order: 0,
        is_featured: 0,
        sections: [],
      }));
    } else {
      loadProject(projectId);
    }
  }, [projectId, categories]);

  const loadProject = async (id) => {
    const res = await adminFetch(ADMIN_ENDPOINTS.fetchProjectAdmin(id));
    const data = await res.json();
    if (data.error) return;
    setEditId(data.id);
    const sections = (data.sections || []).map((sec) => {
      const { columnCount, columnContents } = parseSectionContent(sec.content || '');
      return { ...sec, columnCount, columnContents };
    });
    setForm({
      category_id: data.category_id,
      title: data.title || '',
      summary: data.summary || '',
      image_url: data.image_url || '',
      project_link: data.project_link || '',
      collaborators: data.collaborators || '',
      technology_ids: data.technology_ids || [],
      technology_new: '',
      start_date: data.start_date || '',
      end_date: data.end_date || '',
      sort_order: data.sort_order ?? 0,
      // Ensure is_featured is always a clean 0/1 number
      is_featured: Number(data.is_featured) ? 1 : 0,
      sections,
    });
    setUploadFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminFetch(ADMIN_ENDPOINTS.saveProject, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editId || undefined,
          category_id: form.category_id,
          title: form.title,
          summary: form.summary,
          image_url: form.image_url,
          project_link: form.project_link,
          collaborators: form.collaborators,
          technology_ids: form.technology_ids,
          technology_new: form.technology_new,
          start_date: form.start_date,
          end_date: form.end_date,
          sort_order: form.sort_order,
          is_featured: form.is_featured,
          sections_json: form.sections,
        }),
      });
      const data = await res.json();
      if (data.error) alert(data.error);
      else {
        load();
        setEditId(null);
        setForm({ category_id: '', title: '', summary: '', image_url: '', project_link: '', collaborators: '', technology_ids: [], technology_new: '', start_date: '', end_date: '', sort_order: 0, is_featured: 0, sections: [] });
      }
    } catch (err) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    const res = await adminFetch(ADMIN_ENDPOINTS.deleteProject, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
    const data = await res.json();
    if (data.ok) {
      if (projectId) {
        navigate('/admin/projects');
      } else {
        load();
        setEditId(null);
      }
    } else {
      alert('Delete failed');
    }
  };

  const addSection = () =>
    setForm((f) => ({
      ...f,
      sections: [
        ...(f.sections || []),
        { title: '', content: '', section_type: 'html', sort_order: (f.sections?.length || 0), columnCount: 1, columnContents: [''] },
      ],
    }));

  const updateSection = (i, field, value) =>
    setForm((f) => ({
      ...f,
      sections: (f.sections || []).map((s, j) => (j === i ? { ...s, [field]: value } : s)),
    }));

  const setSectionLayout = (i, newColumnCount) =>
    setForm((f) => {
      const sections = [...(f.sections || [])];
      const sec = sections[i];
      if (!sec) return f;
      const prevCount = sec.columnCount ?? 1;
      const prevContents = sec.columnContents ?? (sec.content ? parseSectionContent(sec.content).columnContents : ['']);
      let nextContents;
      if (newColumnCount === 1) nextContents = [prevContents[0] || ''];
      else if (newColumnCount === 2) nextContents = [prevContents[0] || '', prevContents[1] || ''];
      else nextContents = [prevContents[0] || '', prevContents[1] || '', prevContents[2] || ''];
      const content = buildSectionContent(newColumnCount, nextContents);
      sections[i] = { ...sec, columnCount: newColumnCount, columnContents: nextContents, content };
      return { ...f, sections };
    });

  const updateSectionColumn = (i, colIndex, html) =>
    setForm((f) => {
      const sections = [...(f.sections || [])];
      const sec = sections[i];
      if (!sec) return f;
      const columnCount = sec.columnCount ?? 1;
      const columnContents = [...(sec.columnContents ?? [sec.content || ''])];
      if (columnContents.length < columnCount) while (columnContents.length < columnCount) columnContents.push('');
      columnContents[colIndex] = html;
      const content = buildSectionContent(columnCount, columnContents);
      sections[i] = { ...sec, columnContents, content };
      return { ...f, sections };
    });

  const removeSection = (i) => setForm((f) => ({ ...f, sections: (f.sections || []).filter((_, j) => j !== i) }));

  if (loading) return <p>Loading…</p>;

  return (
    <>
      <h1>Projects</h1>
      <p className="page-subtitle">Manage project categories, technology terms, and portfolio items.</p>

      {!projectId && (
        <div className="card">
          <div className="card-header">
            <h2>Projects</h2>
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => navigate('/admin/projects/new')}
            >
              + Add Project
            </button>
          </div>
          <div className="admin-table-wrap">
            <table>
              <thead><tr><th style={{ width: '28px' }} aria-label="Drag to reorder" /><th>Title</th><th>Category</th><th>Featured</th><th>Actions</th></tr></thead>
              <tbody>
                {projects.map((p, index) => (
                  <tr
                    key={p.id}
                    draggable
                    onDragStart={(e) => {
                      setDragProjectIndex(index);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(index));
                      e.dataTransfer.setData('application/json', JSON.stringify({ index }));
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverProjectIndex(index);
                    }}
                    onDragLeave={() => setDragOverProjectIndex(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragProjectIndex;
                      const to = dragOverProjectIndex ?? index;
                      setDragProjectIndex(null);
                      setDragOverProjectIndex(null);
                      if (from === to || from == null) return;
                      const next = [...projects];
                      const [item] = next.splice(from, 1);
                      next.splice(to, 0, item);
                      setProjects(next);
                      reorderProjects(next).catch((err) => alert(err.message || 'Failed to save order'));
                    }}
                    onDragEnd={() => { setDragProjectIndex(null); setDragOverProjectIndex(null); }}
                    className={dragOverProjectIndex === index ? 'admin-row-drag-over' : ''}
                  >
                    <td className="admin-drag-handle" title="Drag to reorder">⋮⋮</td>
                    <td>
                      <div className="admin-thumb-wrap">
                        {p.imageURL && <img src={imgUrl(p.imageURL)} alt="" className="admin-thumb" onError={(e) => { e.target.style.display = 'none'; }} />}
                        {p.title}
                      </div>
                    </td>
                    <td>{p.category}</td>
                    <td>{p.is_featured ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => navigate(`/admin/projects/${p.id}`)}
                      >
                        Edit
                      </button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(projectId || editId || form.title !== undefined) && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <div className="card-header">
            <h2>{projectId && projectId !== 'new' ? 'Edit Project' : 'Add Project'}</h2>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => navigate('/admin/projects')}
            >
              ← Back to Projects
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label>Title</label><input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required /></div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category_id} onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))} required>
                <option value="">Select</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Summary</label><textarea rows={3} value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} /></div>
            <div className="form-group">
              <label>Image</label>
              <AdminImageUpload
                value={form.image_url}
                onChange={(v) => setForm((f) => ({ ...f, image_url: v }))}
                uploadUrl={ADMIN_ENDPOINTS.upload}
                onUploadSuccess={(path) => setForm((f) => ({ ...f, image_url: path }))}
                previewUrlResolver={imgUrl}
                placeholder="Or enter URL / uploads/..."
              />
            </div>
            <div className="form-group"><label>Project link (slug)</label><input type="text" value={form.project_link} onChange={(e) => setForm((f) => ({ ...f, project_link: e.target.value }))} placeholder="URL slug" /></div>
            <div className="form-group"><label>Collaborators</label><input type="text" value={form.collaborators} onChange={(e) => setForm((f) => ({ ...f, collaborators: e.target.value }))} /></div>
            <div className="form-group">
              <label>Technologies</label>
              <AdminTechnologyTaxonomy
                terms={technologyTerms}
                selectedIds={form.technology_ids || []}
                newTermsString={form.technology_new || ''}
                onChange={({ ids, new: newStr }) =>
                  setForm((f) => ({ ...f, technology_ids: ids, technology_new: newStr }))
                }
                placeholder="Search or type a term, press Enter to add"
              />
            </div>
            <div className="form-row">
              <div className="form-group"><label>Start date</label><input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
              <div className="form-group"><label>End date</label><input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
              <div className="form-group"><label>Sort order</label><input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))} /></div>
              <div className="form-group checkbox-group"><label><input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked ? 1 : 0 }))} /> Featured</label></div>
            </div>
            <div className="form-group">
              <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Content sections</h3>
              {(form.sections || []).map((sec, i) => {
                const columnCount = sec.columnCount ?? parseSectionContent(sec.content || '').columnCount;
                const columnContents = sec.columnContents ?? parseSectionContent(sec.content || '').columnContents;
                const cols = Math.max(1, Math.min(3, columnCount));
                const contents = columnContents.slice(0, cols);
                while (contents.length < cols) contents.push('');
                return (
                  <div key={i} className="card admin-section-card" style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.875rem', color: 'var(--admin-text-muted)' }}>Section title</label>
                    <input type="text" placeholder="Section title" value={sec.title} onChange={(e) => updateSection(i, 'title', e.target.value)} />
                    <div className="admin-section-layout-toolbar">
                      <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', marginRight: '0.5rem' }}>Layout:</span>
                      <button type="button" className={`btn btn-sm ${cols === 1 ? 'btn-success' : 'btn-ghost'}`} onClick={() => setSectionLayout(i, 1)} title="1 column">1 col</button>
                      <button type="button" className={`btn btn-sm ${cols === 2 ? 'btn-success' : 'btn-ghost'}`} onClick={() => setSectionLayout(i, 2)} title="2 columns">2 cols</button>
                      <button type="button" className={`btn btn-sm ${cols === 3 ? 'btn-success' : 'btn-ghost'}`} onClick={() => setSectionLayout(i, 3)} title="3 columns">3 cols</button>
                    </div>
                    <div className={`admin-section-columns admin-section-columns--${cols}`} style={{ gap: '1rem', marginTop: '0.75rem' }}>
                      {contents.map((colHtml, colIdx) => (
                        <div key={colIdx} className="admin-section-column">
                          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                            {cols === 1 ? 'Content' : `Column ${colIdx + 1}`}
                          </label>
                          <AdminSectionEditor
                            value={colHtml}
                            onChange={(content) => updateSectionColumn(i, colIdx, content)}
                            placeholder={cols === 1 ? 'Write section content…' : `Column ${colIdx + 1}…`}
                          />
                        </div>
                      ))}
                    </div>
                    <button type="button" className="btn btn-sm btn-danger" style={{ marginTop: '0.75rem' }} onClick={() => removeSection(i)}>Remove section</button>
                  </div>
                );
              })}
              <button type="button" className="btn btn-sm" onClick={addSection}>+ Add section</button>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-success" disabled={saving}>{editId ? 'Update' : 'Add'} Project</button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  if (projectId) {
                    navigate('/admin/projects');
                  } else {
                    setEditId(null);
                    setForm({
                      category_id: '',
                      title: '',
                      summary: '',
                      image_url: '',
                      project_link: '',
                      collaborators: '',
                      technology_ids: [],
                      technology_new: '',
                      start_date: '',
                      end_date: '',
                      sort_order: 0,
                      is_featured: 0,
                      sections: [],
                    });
                  }
                }}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
